import { FC, useState, useEffect, useRef } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import { useHistory } from 'react-router'
import {
  Formik,
  Field,
  Form,
  FormikHelpers,
  FormikProps,
  ErrorMessage
} from 'formik'
import * as Yup from 'yup'
import { Button } from 'react-bootstrap'

import { useAppDispatch } from 'hooks/store'
import useLoadingDelay from 'hooks/useLoadingDelay'
import { loadingSpinnerPortal } from 'components/atoms/LoadingSpinner'
import Select from 'components/features/common/select/Select'
import {
  shouldNavigateToDisclosureAgreement,
  shouldNavigateToCourseInstructions,
  mainCoursePagePath
} from 'share/utils/courseUtils'
import { CourseOrderItem, OrderCustomerDetails } from 'share/domain/course'
import { ActiveCase } from 'reducers/activecase/activecaseSlice'
import { setVerifiedCoDebtorDetailsTrue } from 'reducers/activecourse/activecourseSlice'
import {
  GET_ORDER_CO_DEBTOR_CUSTOMER_DETAILS,
  GetOrderCoDebtorCustomerDetailsVariables,
  GetOrderCoDebtorCustomerDetailsResponse
} from 'share/queries/courses'
import {
  UPDATE_ORDER_CO_DEBTOR_CUSTOMER_DETAILS,
  UpdateOrderCoDebtorCustomerDetailsVariables,
  UpdateOrderCoDebtorCustomerDetailsResponse,
  UPDATE_ORDER_CO_DEBTOR_VERIFIED_CUSTOMER_DETAILS_FLAG,
  UpdateOrderCoDebtorVerifiedCustomerDetailsFlagVariables,
  UpdateOrderCoDebtorVerifiedCustomerDetailsFlagResponse
} from 'share/mutations/courses'
import { dropdownStateOptions, phoneRegExp } from 'share/utils/utils'
import {
  VerifyInfoFieldsRow,
  VerifyInfoFieldsWrapper
} from 'components/atoms/InputFields/VerifyInfo'
import { TwoBtnsContainer } from 'components/atoms/Layout'

interface CoursesVerifySpouseFormProps {
  activeCase: ActiveCase
  course: CourseOrderItem
}

interface VerifyCoDebtorFormValues {
  city: string
  emailAddress: string
  firstName: string
  homePhone: string
  lastName: string
  mailingAddress: string
  middleName: string
  mobilePhone: string
  selectState: string
  suffix: string
  workPhone: string
  zipCode: string
}

function setInitialValues(
  customerDetails: OrderCustomerDetails
): VerifyCoDebtorFormValues {
  const initialValues: VerifyCoDebtorFormValues = {
    city: customerDetails?.city ?? '',
    emailAddress: customerDetails?.emailAddress ?? '',
    firstName: customerDetails?.firstName ?? '',
    homePhone: customerDetails?.homePhone ?? '',
    lastName: customerDetails?.lastName ?? '',
    mailingAddress: customerDetails?.street ?? '',
    middleName: customerDetails?.middleName ?? '',
    mobilePhone: customerDetails?.mobilePhone ?? '',
    selectState: customerDetails?.state ?? '',
    suffix: customerDetails?.suffix ?? '',
    workPhone: customerDetails?.workPhone ?? '',
    zipCode: customerDetails?.zip ?? ''
  }
  return initialValues
}

function getEmptyInitialValues(): VerifyCoDebtorFormValues {
  const initialValues: VerifyCoDebtorFormValues = {
    city: '',
    emailAddress: '',
    firstName: '',
    homePhone: '',
    lastName: '',
    mailingAddress: '',
    middleName: '',
    mobilePhone: '',
    selectState: '',
    suffix: '',
    workPhone: '',
    zipCode: ''
  }
  return initialValues
}

const validationSchema = Yup.object({
  firstName: Yup.string().required('First Name is Required'),
  lastName: Yup.string().required('Last Name is Required'),
  middleName: Yup.string().max(1),
  suffix: Yup.string(),
  homePhone: Yup.string().matches(
    phoneRegExp,
    'Home phone number is not valid'
  ),
  mobilePhone: Yup.string().matches(
    phoneRegExp,
    'Mobile phone number is not valid'
  ),
  workPhone: Yup.string().matches(
    phoneRegExp,
    'Work phone number is not valid'
  ),
  emailAddress: Yup.string().email('Invalid email format').required('Required'),
  mailingAddress: Yup.string(),
  zipCode: Yup.string(),
  city: Yup.string(),
  selectState: Yup.string().required('State selection required')
})

const CoursesVerifySpouseForm: FC<CoursesVerifySpouseFormProps> = ({
  activeCase,
  course
}) => {
  const history = useHistory()
  const dispatch = useAppDispatch()
  const [loaded, setLoaded] = useState(false)
  const [submitErrorMessage, setSubmitErrorMessage] =
    useState<string | null>(null)

  const formRef = useRef<FormikProps<VerifyCoDebtorFormValues>>(null)

  const { data, loading, error } = useQuery<
    GetOrderCoDebtorCustomerDetailsResponse,
    GetOrderCoDebtorCustomerDetailsVariables
  >(GET_ORDER_CO_DEBTOR_CUSTOMER_DETAILS, {
    variables: {
      request: {
        orderId: course.orderId,
        caseId:
          activeCase.matter && activeCase.matter.id ? activeCase?.matter.id : 0
      }
    }
  })

  const [updateCoDebtorCustomerDetails, { loading: updateLoading }] =
    useMutation<
      UpdateOrderCoDebtorCustomerDetailsResponse,
      UpdateOrderCoDebtorCustomerDetailsVariables
    >(UPDATE_ORDER_CO_DEBTOR_CUSTOMER_DETAILS)

  const [
    updateCoDebtorCustomerVerifiedDetailsFlag,
    { loading: updateVerifiedLoading }
  ] = useMutation<
    UpdateOrderCoDebtorVerifiedCustomerDetailsFlagResponse,
    UpdateOrderCoDebtorVerifiedCustomerDetailsFlagVariables
  >(UPDATE_ORDER_CO_DEBTOR_VERIFIED_CUSTOMER_DETAILS_FLAG)

  useEffect(() => {
    if (data && data.orders && data.orders.orderCoDebtorCustomerDetails) {
      const initial = setInitialValues(data.orders.orderCoDebtorCustomerDetails)

      formRef.current?.setValues(initial)
      setLoaded(true)
    }

    if (error) {
      const emptyInitial = getEmptyInitialValues()
      formRef.current?.setValues(emptyInitial)
      setLoaded(true)
    }
  }, [data, error])

  async function updateCoDebtorCustomerDetailsHelper(
    formValues: VerifyCoDebtorFormValues
  ): Promise<boolean> {
    let result = false
    try {
      const input: OrderCustomerDetails = {
        caseId:
          activeCase.matter && activeCase.matter.id ? activeCase?.matter.id : 0,
        orderId: data?.orders.orderCoDebtorCustomerDetails.orderId
          ? data?.orders.orderCoDebtorCustomerDetails.orderId
          : 0,
        orderKey: data?.orders.orderCoDebtorCustomerDetails.orderKey
          ? data?.orders.orderCoDebtorCustomerDetails.orderKey
          : 0,
        personId: data?.orders.orderCoDebtorCustomerDetails.personId
          ? data?.orders.orderCoDebtorCustomerDetails.personId
          : 0,
        firstName: formValues.firstName,
        lastName: formValues.lastName,
        middleName: formValues.middleName,
        suffix: formValues.suffix,
        homePhone: formValues.homePhone,
        mobilePhone: formValues.mobilePhone,
        workPhone: formValues.workPhone,
        emailAddress: formValues.emailAddress,
        street: formValues.mailingAddress,
        city: formValues.city,
        state: formValues.selectState,
        zip: formValues.zipCode
      }

      const updateResult = await updateCoDebtorCustomerDetails({
        variables: { coDebtorDetails: input }
      })
      if (
        updateResult.data?.orders.updateOrderCoDebtorCustomerDetails.success
      ) {
        result = true
      }
    } catch (e) {
      result = false
    }

    if (!result) {
      setSubmitErrorMessage('Error saving the coDebtor customer details')
    }

    return result
  }

  async function updateCoDebtorCustomerVerifiedDetailsFlagHelper(): Promise<boolean> {
    let result = false
    try {
      const input = {
        flag: true,
        orderId: data?.orders.orderCoDebtorCustomerDetails.orderId
          ? data.orders.orderCoDebtorCustomerDetails.orderId
          : 0,
        caseId:
          activeCase.matter && activeCase.matter.id ? activeCase?.matter.id : 0
      }

      const updateResult = await updateCoDebtorCustomerVerifiedDetailsFlag({
        variables: { request: input }
      })
      if (
        updateResult.data?.orders.updateOrderCoDebtorVerifiedCustomerDetailsFlag
          .success
      ) {
        result = true
        dispatch(setVerifiedCoDebtorDetailsTrue(course.courseNumber))
      }
    } catch (e) {
      result = false
    }

    return result
  }

  function onSubmitWrapper(
    formik: FormikProps<VerifyCoDebtorFormValues>,
    e: React.FormEvent<HTMLFormElement>
  ) {
    e.preventDefault()
    // only want to save data if something has changed
    if (formik.dirty) {
      formik.handleSubmit(e)
    } else {
      handleNavigationToNextPage()
    }
  }

  async function handleNavigationToNextPage() {
    // update verified details flag before navigating to the next page
    await updateCoDebtorCustomerVerifiedDetailsFlagHelper()
    let path: string | null = null
    if ((path = shouldNavigateToDisclosureAgreement(course))) {
      history.push(path)
    } else if ((path = shouldNavigateToCourseInstructions(course))) {
      history.push(path)
    } else {
      history.push(mainCoursePagePath())
    }
  }

  async function handleBackButtonClick() {
    history.push({
      pathname: '/dashboard/courses'
    })
  }

  async function onSubmit(
    values: VerifyCoDebtorFormValues,
    { setSubmitting }: FormikHelpers<VerifyCoDebtorFormValues>
  ): Promise<void> {
    const updateResult = await updateCoDebtorCustomerDetailsHelper(values)
    setSubmitting(false)
    if (updateResult) {
      handleNavigationToNextPage()
    }
  }

  const loadings = loading || !loaded || updateLoading || updateVerifiedLoading

  const loadingDelay = useLoadingDelay(loadings)

  if (error) {
    return <div>{error?.message}</div>
  }

  return (
    <>
      {loadingDelay && loadingSpinnerPortal()}
      <Formik
        initialValues={
          validationSchema.getDefault() as VerifyCoDebtorFormValues
        }
        innerRef={formRef}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {(formik) => (
          <Form onSubmit={(e) => onSubmitWrapper(formik, e)}>
            <VerifyInfoFieldsWrapper
              id='primaryCustomerDetails'
              data-testid='course-verify-spouse-info-form'
            >
              <VerifyInfoFieldsRow>
                <div className='form-group'>
                  <label htmlFor='firstName'>First Name</label>
                  <Field
                    autoFocus
                    className={
                      formik.errors.firstName
                        ? 'form-control is-invalid'
                        : 'form-control'
                    }
                    id='firstName'
                    data-testid='course-verify-spouse-info-first-name-field'
                    name='firstName'
                    placeholder=''
                  />
                  <ErrorMessage
                    name='firstName'
                    render={(msg) => (
                      <span
                        className='text-danger'
                        data-testid='course-verify-spouse-info-first-name-field-error'
                      >
                        {msg}
                      </span>
                    )}
                  />
                </div>
                <div className='form-group'>
                  <label htmlFor='lastName'>Last Name</label>
                  <Field
                    className={
                      formik.errors.lastName
                        ? 'form-control is-invalid'
                        : 'form-control'
                    }
                    id='lastName'
                    name='lastName'
                    data-testid='course-verify-spouse-info-last-name-field'
                    placeholder=''
                    type='text'
                  />
                  <ErrorMessage
                    name='lastName'
                    render={(msg) => (
                      <span
                        className='text-danger'
                        data-testid='course-verify-spouse-info-last-name-field-error'
                      >
                        {msg}
                      </span>
                    )}
                  />
                </div>
              </VerifyInfoFieldsRow>

              <VerifyInfoFieldsRow>
                <div className='form-group'>
                  <label htmlFor='middleName'>Middle Initial</label>
                  <Field
                    className='form-control'
                    id='middleName'
                    maxLength={1}
                    name='middleName'
                    data-testid='course-verify-spouse-info-middle-name-field'
                    placeholder='Optional'
                    type='text'
                  />
                  <ErrorMessage
                    name='middleName'
                    render={(msg) => (
                      <span
                        className='text-danger'
                        data-testid='course-verify-spouse-info-middle-name-field-error'
                      >
                        {msg}
                      </span>
                    )}
                  />
                </div>
                <div className='form-group'>
                  <label htmlFor='suffix'>Suffix</label>
                  <Field
                    className='form-control'
                    id='suffix'
                    name='suffix'
                    data-testid='course-verify-spouse-info-suffix-field'
                    placeholder='Optional'
                    type='text'
                  />
                  <ErrorMessage
                    name='suffix'
                    render={(msg) => (
                      <span
                        className='text-danger'
                        data-testid='course-verify-spouse-info-suffix-field-error'
                      >
                        {msg}
                      </span>
                    )}
                  />
                </div>
              </VerifyInfoFieldsRow>

              <VerifyInfoFieldsRow>
                <div className='form-group'>
                  <label htmlFor='homePhone'>Home Phone</label>
                  <Field
                    className={
                      formik.errors.homePhone
                        ? 'form-control is-invalid'
                        : 'form-control'
                    }
                    id='homePhone'
                    name='homePhone'
                    data-testid='course-verify-spouse-info-home-phone-field'
                    placeholder=''
                    type='text'
                  />
                  <ErrorMessage
                    name='homePhone'
                    render={(msg) => (
                      <span
                        className='text-danger'
                        data-testid='course-verify-spouse-info-home-phone-field-error'
                      >
                        {msg}
                      </span>
                    )}
                  />
                </div>

                <div className='form-group'>
                  <label htmlFor='mobilePhone'>Mobile Phone</label>
                  <Field
                    readOnly
                    className={
                      formik.errors.mobilePhone
                        ? 'form-control is-invalid'
                        : 'form-control'
                    }
                    id='mobilePhone'
                    name='mobilePhone'
                    data-testid='course-verify-spouse-info-mobile-phone-field'
                    placeholder='Optional'
                    type='text'
                  />
                  <ErrorMessage
                    name='mobilePhone'
                    render={(msg) => (
                      <span
                        className='text-danger'
                        data-testid='course-verify-spouse-info-mobile-phone-field-error'
                      >
                        {msg}
                      </span>
                    )}
                  />
                </div>
                <div className='form-group'>
                  <label htmlFor='workPhone'>Work Phone</label>
                  <Field
                    className={
                      formik.errors.workPhone
                        ? 'form-control is-invalid'
                        : 'form-control'
                    }
                    id='workPhone'
                    name='workPhone'
                    data-testid='course-verify-spouse-info-work-phone-field'
                    placeholder='Optional'
                    type='text'
                  />
                  <ErrorMessage
                    name='workPhone'
                    render={(msg) => (
                      <span
                        className='text-danger'
                        data-testid='course-verify-spouse-info-work-phone-field-error'
                      >
                        {msg}
                      </span>
                    )}
                  />
                </div>
              </VerifyInfoFieldsRow>

              <VerifyInfoFieldsRow>
                <div className='form-group'>
                  <label htmlFor='emailAddress'>Email Address</label>
                  <Field
                    readOnly
                    className={
                      formik.errors.emailAddress
                        ? 'form-control is-invalid'
                        : 'form-control'
                    }
                    id='emailAddress'
                    name='emailAddress'
                    data-testid='course-verify-spouse-info-email-address-field'
                    placeholder=''
                    type='email'
                  />
                  <ErrorMessage
                    name='emailAddress'
                    render={(msg) => (
                      <span
                        className='text-danger'
                        data-testid='course-verify-spouse-info-email-address-field-error'
                      >
                        {msg}
                      </span>
                    )}
                  />
                </div>
              </VerifyInfoFieldsRow>

              <VerifyInfoFieldsRow>
                <div className='form-group'>
                  <label htmlFor='mailingAddress'>Street Address</label>
                  <Field
                    as='textarea'
                    className='form-control'
                    id='mailingAddress'
                    name='mailingAddress'
                    data-testid='course-verify-spouse-info-mailing-address-field'
                    placeholder='Optional'
                  />
                  <ErrorMessage
                    name='mailingAddress'
                    render={(msg) => (
                      <span
                        className='text-danger'
                        data-testid='course-verify-spouse-info-mailing-address-field-error'
                      >
                        {msg}
                      </span>
                    )}
                  />
                </div>
              </VerifyInfoFieldsRow>

              <VerifyInfoFieldsRow>
                <div className='form-group'>
                  <label htmlFor='zipCode'>Zip Code</label>
                  <Field
                    className='form-control'
                    id='zipCode'
                    name='zipCode'
                    data-testid='course-verify-spouse-info-zip-code-field'
                    placeholder='Optional'
                    type='text'
                  />
                  <ErrorMessage
                    name='zipCode'
                    render={(msg) => (
                      <span
                        className='text-danger'
                        data-testid='course-verify-spouse-info-zip-code-field-error'
                      >
                        {msg}
                      </span>
                    )}
                  />
                </div>
                <div className='form-group'>
                  <label htmlFor='city'>City</label>
                  <Field
                    type='text'
                    id='city'
                    name='city'
                    data-testid='course-verify-spouse-info-city-field'
                    placeholder='Optional'
                    className='form-control'
                  />
                  <ErrorMessage
                    name='city'
                    render={(msg) => (
                      <span
                        className='text-danger'
                        data-testid='course-verify-spouse-info-city-field-error'
                      >
                        {msg}
                      </span>
                    )}
                  />
                </div>
                <Select
                  fieldStyling={
                    formik.errors.selectState
                      ? 'form-control is-invalid'
                      : 'form-control'
                  }
                  label='State'
                  name='selectState'
                  data-testid='course-verify-spouse-info-select-state-field'
                  options={dropdownStateOptions}
                  selectStyling='form-group'
                >
                  <ErrorMessage
                    name='selectState'
                    render={(msg) => (
                      <span
                        className='text-danger'
                        data-testid='course-verify-spouse-info-select-state-field-error'
                      >
                        {msg}
                      </span>
                    )}
                  />
                </Select>
              </VerifyInfoFieldsRow>

              {submitErrorMessage && (
                <span
                  className='text-danger'
                  data-testid='course-verify-spouse-info-submit-error-message'
                >
                  {submitErrorMessage}
                </span>
              )}
            </VerifyInfoFieldsWrapper>

            <TwoBtnsContainer>
              <Button
                variant='outline-secondary'
                onClick={handleBackButtonClick}
                data-testid='course-verify-spouse-info-back-button'
              >
                Back
              </Button>
              <Button
                type='submit'
                variant='primary'
                data-testid='course-verify-spouse-info-submit-button'
              >
                Submit
              </Button>
            </TwoBtnsContainer>
          </Form>
        )}
      </Formik>
    </>
  )
}

export default CoursesVerifySpouseForm
